import { type FC, useState } from 'react';
import { Comment } from '@/types';
import { Button } from './ui/button';
import { Avatar } from './ui/avatar';
import { Badge } from './ui/badge';
import { Edit2, Trash2, Save, X, Reply, MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Tooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import { useAuth } from '@/auth';
import { checkAllPermissions, PERMISSIONS } from '@/auth/permissions';

interface CommentItemProps {
  comment: Comment;
  depth?: number;
  currentUserId?: number;
  onReply: (commentId: number) => void;
  onEdit: (commentId: number, newComment: string) => void;
  onDelete: (commentId: number) => void;
  replyingTo: number | null;
  onCancelReply: () => void;
  replyText: string;
  onReplyTextChange: (text: string) => void;
  onSubmitReply: (parentId: number) => void;
  isAddingReply: boolean;
  getRoleBadge: () => { variant: any; text: string; className?: string };
  formatDate: (date: string) => string;
  maxLength?: number;
}

const CommentItem: FC<CommentItemProps> = ({
  comment,
  depth = 0,
  currentUserId,
  onReply,
  onEdit,
  onDelete,
  replyingTo,
  onCancelReply,
  replyText,
  onReplyTextChange,
  onSubmitReply,
  isAddingReply,
  getRoleBadge,
  formatDate,
  maxLength = 1000
}) => {
  const { user } = useAuth();
  const userPermissions = user?.permissions ?? [];
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.comment);
  const [isSaving, setIsSaving] = useState(false);

  const canEdit = currentUserId === comment.user.id || checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_COMMENTS.name]);
  const canDelete = currentUserId === comment.user.id || checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_COMMENTS.name]);
  const roleBadge = getRoleBadge();


  const handleSaveEdit = async () => {
    if (editText.trim() && editText !== comment.comment && editText.length <= maxLength) {
      setIsSaving(true);
      try {
        await onEdit(comment.id, editText);
        setIsEditing(false);
      } finally {
        setIsSaving(false);
      }
    } else {
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditText(comment.comment);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este comentario?')) {
      onDelete(comment.id);
    }
  };

  return (
    <div className={`${depth > 0 ? 'ml-8 mt-4 border-l-2 border-slate-200 pl-4' : 'p-4'}`}>
      <div className="flex space-x-4">
        <Avatar className="w-11 h-11 bg-blue-500 flex items-center justify-center shadow-md">
          <span className="text-sm font-medium text-white">
            {comment.user.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
          </span>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2 flex-wrap">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-semibold text-slate-800">{comment.user.name}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <span>{comment.user.email}</span>
                </TooltipContent>
              </Tooltip>
              <Badge
                variant={roleBadge.variant}
                className={`text-xs ${roleBadge.className || ''}`}
              >
                {roleBadge.text}
              </Badge>

              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-sm text-slate-500">{formatDate(comment.updated_at)}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <span>{new Date(comment.updated_at).toLocaleString()}</span>
                </TooltipContent>
              </Tooltip>

              {comment.updated_at !== comment.created_at && (
                <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-full">(editado)</span>
              )}
            </div>

            {(canEdit || canDelete) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-slate-100">
                    <MoreVertical className="h-4 w-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="border border-slate-200 shadow-lg">
                  {canEdit && (
                    <DropdownMenuItem onClick={() => setIsEditing(true)} className="hover:bg-slate-50">
                      <Edit2 className="mr-2 h-4 w-4" />
                      Editar
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <DropdownMenuItem onClick={handleDelete} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Eliminar
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-3 bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="relative">
                <textarea
                  value={editText}
                  onChange={(e) => {
                    if (e.target.value.length <= maxLength) {
                      setEditText(e.target.value);
                    }
                  }}
                  className="w-full p-3 border border-slate-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  rows={3}
                />
                <div className="absolute bottom-3 right-3 text-xs text-slate-500">
                  {editText.length}/{maxLength}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="text-xs text-slate-500">
                  {editText.length > maxLength - 50 && (
                    <span className={editText.length >= maxLength ? "text-red-500 font-medium" : "text-amber-600"}>
                      {editText.length >= maxLength
                        ? "Límite alcanzado"
                        : `${maxLength - editText.length} caracteres restantes`
                      }
                    </span>
                  )}
                </div>
                <div className="flex space-x-2">
                  <Button size="sm" onClick={handleSaveEdit} loading={isSaving} disabled={!editText.trim() || editText.length > maxLength} className="bg-blue-600 hover:bg-blue-700">
                    <Save size={14} className="mr-1" />
                    Guardar
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCancelEdit} className="border-slate-300 hover:bg-slate-100">
                    <X size={14} className="mr-1" />
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-3">
                <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{comment.comment}</p>
              </div>
              <button
                onClick={() => onReply(comment.id)}
                className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 transition-colors hover:bg-blue-50 px-2 py-1 rounded-md"
              >
                <Reply size={14} />
                <span>Responder</span>
              </button>
            </>
          )}

          {replyingTo === comment.id && (
            <div className="mt-4 space-y-3 bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="relative">
                <textarea
                  value={replyText}
                  onChange={(e) => onReplyTextChange(e.target.value)}
                  placeholder="Escribe tu respuesta..."
                  className="w-full p-3 border border-slate-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  rows={3}
                />
                <div className="absolute bottom-3 right-3 text-xs text-slate-500">
                  {replyText.length}/{maxLength}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="text-xs text-slate-500">
                  {replyText.length > maxLength - 50 && (
                    <span className={replyText.length >= maxLength ? "text-red-500 font-medium" : "text-amber-600"}>
                      {replyText.length >= maxLength
                        ? "Límite alcanzado"
                        : `${maxLength - replyText.length} caracteres restantes`
                      }
                    </span>
                  )}
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => onSubmitReply(comment.id)}
                    disabled={!replyText.trim() || isAddingReply || replyText.length > maxLength}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Reply size={14} className="mr-1" />
                    Responder
                  </Button>
                  <Button variant="outline" size="sm" onClick={onCancelReply} className="border-slate-300 hover:bg-slate-100">
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {comment.children.map(child => (
        <CommentItem
          key={child.id}
          comment={child}
          depth={depth + 1}
          currentUserId={currentUserId}
          onReply={onReply}
          onEdit={onEdit}
          onDelete={onDelete}
          replyingTo={replyingTo}
          onCancelReply={onCancelReply}
          replyText={replyText}
          onReplyTextChange={onReplyTextChange}
          onSubmitReply={onSubmitReply}
          isAddingReply={isAddingReply}
          getRoleBadge={getRoleBadge}
          formatDate={formatDate}
          maxLength={maxLength}
        />
      ))}
    </div>
  );
};

export default CommentItem;
